import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { Check, X, Edit, Loader2, AlertCircle } from 'lucide-react';

interface InlineEditToggleProps {
  value: boolean;
  onSave: (newValue: boolean) => Promise<void>;
  onCancel?: () => void;
  label?: string;
  trueLabel?: string;
  falseLabel?: string;
  className?: string;
  disabled?: boolean;
  displayAs?: 'badge' | 'text';
}

export const InlineEditToggle: React.FC<InlineEditToggleProps> = ({
  value,
  onSave,
  onCancel,
  label,
  trueLabel = 'Yes',
  falseLabel = 'No',
  className = '',
  disabled = false,
  displayAs = 'badge'
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update selected value when prop value changes
  useEffect(() => {
    if (!isEditing) {
      setSelectedValue(value);
    }
  }, [value, isEditing]);

  const handleEdit = () => {
    if (disabled) return;
    setIsEditing(true);
    setError(null);
    setSelectedValue(value);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedValue(value);
    setError(null);
    onCancel?.();
  };

  const handleSave = async () => {
    // No change, just exit editing mode
    if (selectedValue === value) {
      setIsEditing(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onSave(selectedValue);
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setIsLoading(false);
    }
  };

  const renderDisplayValue = () => {
    if (displayAs === 'badge') {
      return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value 
            ? 'bg-green-100 text-green-800' 
            : 'bg-yellow-100 text-yellow-800'
        }`}>
          {value ? trueLabel : falseLabel}
        </span>
      );
    }
    
    return <span>{value ? trueLabel : falseLabel}</span>;
  };

  if (!isEditing) {
    return (
      <div className={`group ${className}`}>
        {label && (
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        <div className="relative">
          <div 
            className={`text-sm text-gray-900 bg-gray-50 rounded-md px-3 py-2 min-h-[40px] flex items-center cursor-pointer group-hover:bg-gray-100 transition-colors ${
              disabled ? 'cursor-not-allowed opacity-50' : ''
            }`}
            onClick={handleEdit}
          >
            <div className="flex items-center justify-between w-full">
              {renderDisplayValue()}
              {!disabled && (
                <Edit className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="space-y-2">
        <div className="bg-gray-50 rounded-md px-3 py-2">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="toggle-option"
                checked={selectedValue === true}
                onChange={() => setSelectedValue(true)}
                disabled={isLoading}
                className="mr-2"
              />
              <span className="text-sm">{trueLabel}</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="toggle-option"
                checked={selectedValue === false}
                onChange={() => setSelectedValue(false)}
                disabled={isLoading}
                className="mr-2"
              />
              <span className="text-sm">{falseLabel}</span>
            </label>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleSave}
            disabled={isLoading}
            className="px-3 py-1 h-8"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                <span className="text-xs">Saving...</span>
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-1" />
                <span className="text-xs">Save</span>
              </>
            )}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
            className="px-3 py-1 h-8"
          >
            <X className="w-4 h-4 mr-1" />
            <span className="text-xs">Cancel</span>
          </Button>
        </div>
        {error && (
          <div className="flex items-center space-x-1 text-red-600 text-xs">
            <AlertCircle className="w-3 h-3 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </div>
  );
};
